import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, SplitText);

window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;
// window.SplitText = SplitText;
/**
 * Clase utilitaria .parallax
 * Atributos data:
 *  - data-speed (default: 0.5) — velocidad del parallax
 *  - data-speed-mobile (default: igual a data-speed) — velocidad en móvil
 *  - data-direction (default: "vertical") — "vertical" | "horizontal"
 */
export function initParallax() {
  const elements = document.querySelectorAll('.parallax');
  const isMobile = window.innerWidth < 768;

  elements.forEach((el) => {
    const parsedSpeedDesktop = parseFloat(el.dataset.speed);
    const speedDesktop = Number.isFinite(parsedSpeedDesktop) ? parsedSpeedDesktop : 0.5;
    const parsedSpeedMobile = parseFloat(el.dataset.speedMobile);
    const speedMobile = Number.isFinite(parsedSpeedMobile) ? parsedSpeedMobile : speedDesktop;
    const speed = isMobile ? speedMobile : speedDesktop;
    const direction = el.dataset.direction || 'vertical';

    if (speed === 0) return;

    const props = direction === 'horizontal' 
      ? { x: () => window.innerWidth * speed * -1 }
      : { y: () => window.innerHeight * speed * -1 };

    gsap.set(el, { willChange: 'transform' });

    gsap.to(el, {
      ...props,
      ease: 'none',
      scrollTrigger: {
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 0.6,
      },
    });
  });
}

/**
 * Clase utilitaria .anim
 * 
 * Modos (data-mode):
 *  - "scroll" (default): animación sincronizada con scrub
 *  - "trigger": animación se reproduce una vez al entrar en viewport
 *
 * Data-attributes opcionales:
 *  - data-anim="top|bottom|left|right" — dirección de entrada
 *  - data-mode="scroll|trigger"
 *  - data-mobile="false" — deshabilita la animación en móvil
 *  - data-child="true|false" — alias de data-children
 *  - data-children="true" — anima SOLO los hijos directos (con stagger), no el padre
 *  - data-children="false" — anima SOLO el padre, no los hijos
 *  - (sin data-children) — anima SOLO el padre (default)
 *  - data-translate="2" — distancia de traslación en REMs (default: 2)
 *  - data-ease="power2.out" — tipo de easing (default: power2.out)
 *  - data-start (default: "top 85%")
 *  - data-end (default: "top 50%") — solo en modo scroll
 *  - data-scrub (default: 2) — solo en modo scroll
 *  - data-stagger (default: 0.1)
 *  - data-duration (default: 0.8)
 *  - data-delay (default: 0) — solo en modo trigger
 *  - data-toggle (default: "play none none none") — solo en modo trigger
 */

export function initAnimations() {
  const elements = document.querySelectorAll('.anim');
  const isMobile = window.innerWidth < 768;
  const parseDataNumber = (value, fallback) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  elements.forEach((el) => {
    const mobileEnabled = el.dataset.mobile !== 'false';
    
    if (isMobile && !mobileEnabled) return;

    const direction = el.dataset.anim || 'bottom';
    const mode = el.dataset.mode || 'trigger';
    const childrenAttr = el.dataset.children ?? el.dataset.child;
    const translate = parseDataNumber(el.dataset.translate, 2);
    const ease = el.dataset.ease || 'power2.out';
    const start = el.dataset.start || 'top 85%';
    const end = el.dataset.end || 'top 50%';
    const scrub = parseDataNumber(el.dataset.scrub, 2);
    const stagger = parseDataNumber(el.dataset.stagger, 0.1);
    const duration = parseDataNumber(el.dataset.duration, 0.8);
    const delay = parseDataNumber(el.dataset.delay, 0);
    const toggle = el.dataset.toggle || 'play none none none';

    const fromProps = getDirectionProps(direction, translate);

    let targets;
    let useStagger = false;

    if (childrenAttr === 'true') {
      targets = Array.from(el.children);
      if (!targets.length) {
        targets = el;
      } else {
        useStagger = targets.length > 1;
        gsap.set(el, { opacity: 1 });
        gsap.set(targets, { ...fromProps, opacity: 0 });
      }
    } else if (childrenAttr === 'false') {
      targets = el;
      useStagger = false;
      gsap.set(targets, fromProps);
    } else {
      targets = el;
      useStagger = false;
      gsap.set(targets, fromProps);
    }

    if (mode === 'scroll') {
      gsap.to(targets, {
        x: 0,
        y: 0,
        opacity: 1,
        duration,
        ease,
        stagger: useStagger ? stagger : 0,
        scrollTrigger: {
          trigger: el,
          start,
          end,
          scrub,
        },
      });
    } else if (mode === 'load') {
      gsap.to(targets, {
        x: 0,
        y: 0,
        opacity: 1,
        duration,
        delay,
        ease,
        stagger: useStagger ? stagger : 0,
      });
    } else {
      gsap.to(targets, {
        x: 0,
        y: 0,
        opacity: 1,
        duration,
        delay,
        ease,
        stagger: useStagger ? stagger : 0,
        scrollTrigger: {
          trigger: el,
          start,
          toggleActions: toggle,
        },
      });
    }
  });
}

/**
 * Obtiene las propiedades de transformación según la dirección
 * @param {string} direction - Dirección de la animación
 * @param {number} translate - Distancia en REMs
 */
function getDirectionProps(direction, translate = 3) {
  const distance = translate * 16;

  switch (direction) {
    case 'top':
      return { y: -distance };
    case 'bottom':
      return { y: distance };
    case 'left':
      return { x: -distance };
    case 'right':
      return { x: distance };
    default:
      return { y: distance };
  }
}

/**
 * Anima el trazo de SVGs con clase .svg-stroke-anim
 * de forma sincronizada con el scroll.
 *
 * Data-attributes opcionales (sobre el SVG):
 * - data-start (default: "top 85%")
 * - data-end (default: "bottom top")
 * - data-scrub (default: 1)
 * - data-stroke-direction ("normal" | "reverse")
 */
export function initStrokeSvgAnim() {
  const svgs = document.querySelectorAll('.svg-stroke-anim')
  if (!svgs.length) return

  svgs.forEach((svg) => {
    const paths = svg.querySelectorAll('path, line, polyline, polygon, circle, rect, ellipse')
    if (!paths.length) return

    const start = svg.dataset.start || 'top 95%'
    const end = svg.dataset.end || 'bottom 50%'
    const scrub = parseFloat(svg.dataset.scrub) || 1
    const direction = (svg.dataset.strokeDirection || 'normal').trim().toLowerCase()
    const isReverse = direction === 'reverse'

    const tl = gsap.timeline({ paused: true })

    paths.forEach((path) => {
      if (typeof path.getTotalLength !== 'function') return

      const length = path.getTotalLength()
      if (!Number.isFinite(length) || length <= 0) return

      gsap.set(path, {
        strokeDasharray: length,
        strokeDashoffset: isReverse ? -length : length,
        willChange: 'stroke-dashoffset'
      })

      tl.to(path, {
        strokeDashoffset: 0,
        ease: 'none',
        duration: 1
      }, 0)
    })

    if (!tl.getChildren().length) return

    ScrollTrigger.create({
      trigger: svg,
      start,
      end,
      scrub,
      animation: tl,
      invalidateOnRefresh: true
    })
  })
}

function getClipPathPreset(preset = 'center') {
  const value = (preset || 'center').trim().toLowerCase()

  switch (value) {
    case 'left':
    case 'left-right':
    case 'left-to-right':
    case 'ltr':
      return {
        closed: 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)',
        open: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'
      }

    case 'right':
    case 'right-left':
    case 'right-to-left':
    case 'rtl':
      return {
        closed: 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)',
        open: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'
      }

    case 'bottom':
    case 'bottom-top':
    case 'bottom-to-top':
    case 'btt':
      return {
        closed: 'polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)',
        open: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'
      }

    case 'top':
    case 'top-bottom':
    case 'top-to-bottom':
    case 'ttb':
      return {
        closed: 'polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)',
        open: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'
      }

    case 'center':
    case 'center-out':
    case 'center-to-edges':
    default:
      return {
        closed: 'polygon(50% 50%, 50% 50%, 50% 50%, 50% 50%)',
        open: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'
      }
  }
}

/**
 * Refresh ScrollTrigger para recalcular posiciones
 */
export function refreshScrollTrigger() {
  ScrollTrigger.refresh();
}

export function revealTxt() {
  const elements = document.querySelectorAll('.reveal')
  if (!elements.length) return

  elements.forEach((el) => {
    const mobileAttr = (el.dataset.mobile || 'true').trim().toLowerCase()
    const allowMobile = mobileAttr !== 'false'
    const isMobile = window.deviceData?.isMobile ?? window.matchMedia('(max-width: 991px)').matches

    if (!allowMobile && isMobile) {
      el.style.opacity = '1'
      return
    }

    const style = (el.dataset.style || 'move').trim().toLowerCase()
    const mode = el.dataset.mode || 'trigger'
    const startVal = el.dataset.start || 'top 85%'
    const endVal = el.dataset.end || 'top 50%'
    const scrubValue = parseFloat(el.dataset.scrub) || 2
    const duration = parseFloat(el.dataset.duration) || 0.8
    const delay = parseFloat(el.dataset.delay) || 0
    const toggleActions = el.dataset.toggle || 'play none none none'

    if (style === 'clip-path') {
      const type = (el.dataset.type || 'line').trim().toLowerCase()
      const isLetterMode = ['letter', 'letters', 'char', 'chars'].includes(type)
      const splitType = isLetterMode ? 'chars' : 'lines'
      const text = new SplitText(el, {
        type: splitType,
        linesClass: 'split-line',
        charsClass: 'split-char'
      })
      const targets = isLetterMode ? text.chars : text.lines
      const stagger = parseFloat(el.dataset.stagger) || 0.1
      const fromValue = el.dataset.from || '100'

      el.style.opacity = '1'

      targets.forEach((item) => {
        const parent = item.parentNode
        if (!parent) return

        if (parent.classList?.contains('split-wrap')) {
          return
        }

        const wrapper = document.createElement('div')
        wrapper.classList.add('split-wrap')
        wrapper.style.overflow = 'hidden'
        if (isLetterMode) {
          wrapper.style.display = 'inline-block'
        }
        parent.insertBefore(wrapper, item)
        wrapper.appendChild(item)
      })

      gsap.set(targets, { yPercent: fromValue })

      if (mode === 'scroll') {
        gsap.to(targets, {
          yPercent: 0,
          y: '0',
          stagger,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el,
            start: startVal,
            end: endVal,
            scrub: scrubValue,
          }
        })
      } else {
        gsap.to(targets, {
          yPercent: 0,
          duration,
          delay,
          stagger,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el,
            start: startVal,
            toggleActions,
          }
        })
      }

      return
    }

    const fromLeft = el.dataset.from || '-3rem'
    const stagger = parseFloat(el.dataset.stagger) || 0
    const children = Array.from(el.children)
    const targets = children.length ? children : [el]

    targets.forEach((target) => {
      if (window.getComputedStyle(target).position === 'static') {
        gsap.set(target, { position: 'relative' })
      }
    })

    gsap.set(targets, {
      opacity: 0,
      left: fromLeft,
    })

    if (mode === 'scroll') {
      gsap.to(targets, {
        opacity: 1,
        left: '0',
        stagger,
        ease: 'power1.out',
        scrollTrigger: {
          trigger: el,
          start: startVal,
          end: endVal,
          scrub: scrubValue,
        }
      })
    } else {
      gsap.to(targets, {
        opacity: 1,
        left: '0',
        duration,
        delay,
        stagger,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: el,
          start: startVal,
          toggleActions,
        }
      })
    }
  })
}

// export function revealLines() {
//   revealTxt('line')
// }

export function clipBox() {
  const boxes = document.querySelectorAll('.clipbox')
  if (!boxes.length) return

  boxes.forEach((box) => {
    const clipPreset = box.dataset.clippath || 'center'
    const { closed: clipClosed, open: clipOpen } = getClipPathPreset(clipPreset)
    const mode = box.dataset.mode || 'trigger'
    const start = box.dataset.start || 'top 50%'
    const end = box.dataset.end || 'bottom 15%'
    const scrub = parseFloat(box.dataset.scrub) || 1
    const toggleActions = box.dataset.toggle || 'play none none none'
    const duration = parseFloat(box.dataset.duration) || 1.5
    const delay = parseFloat(box.dataset.delay) || 0
    const ease = box.dataset.ease || 'power4.out'
    const candidateImage = box.querySelector('img')
    const image = candidateImage && !candidateImage.closest('.row-doc')
      ? candidateImage
      : null

    gsap.set(box, {
      translateY: '3rem',
      clipPath: clipClosed,
      webkitClipPath: clipClosed,
      willChange: 'clip-path'
    })

    if (image) {
      gsap.set(image, {
        scale: 1,
        transformOrigin: '50% 50%',
        willChange: 'transform'
      })
    }

    const tl = gsap.timeline({ paused: true })

    tl.to(box, {
      translateY: "0",
      clipPath: clipOpen,
      webkitClipPath: clipOpen,
      duration,
      delay,
      ease,
      overwrite: 'auto'
    }, 0)

    if (image) {
      tl.to(image, {
        scale: 1.2,
        duration,
        ease,
        overwrite: 'auto'
      }, 0)
    }

    if (mode === 'scroll') {
      ScrollTrigger.create({
        trigger: box,
        start,
        end,
        scrub,
        animation: tl,
        invalidateOnRefresh: true
      })
    } else {
      ScrollTrigger.create({
        trigger: box,
        start,
        end,
        animation: tl,
        toggleActions,
        invalidateOnRefresh: true
      })
    }
  })
}

export function clipLine() {
  const lines = document.querySelectorAll('.clipline')
  if (!lines.length) return

  // Sintaxis normalizada para evitar conflictos al interpolar polygon() en GSAP
  const clipClosed = 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)'
  const clipOpen = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'

  gsap.set(lines, {
    clipPath: clipClosed,
    webkitClipPath: clipClosed,
    willChange: 'clip-path'
  })

  lines.forEach((line) => {
    const mode = line.dataset.mode || 'trigger'
    const start = line.dataset.start || 'top 50%'
    const end = line.dataset.end || 'bottom 15%'
    const scrub = parseFloat(line.dataset.scrub) || 1
    const duration = parseFloat(line.dataset.duration) || 1.5
    const delay = parseFloat(line.dataset.delay) || 0
    const ease = line.dataset.ease || 'power4.out'
    const toggleActions = line.dataset.toggle || 'play none none none'

    const tl = gsap.timeline({ paused: true })

    tl.to(line, {
      clipPath: clipOpen,
      webkitClipPath: clipOpen,
      duration,
      delay,
      ease,
      overwrite: 'auto'
    }, 0)

    if (mode === 'scroll') {
      ScrollTrigger.create({
        trigger: line,
        start,
        end,
        scrub,
        animation: tl,
        invalidateOnRefresh: true
      })
    } else {
      ScrollTrigger.create({
        trigger: line,
        start,
        end,
        animation: tl,
        toggleActions,
        invalidateOnRefresh: true
      })
    }
  })
}

/**
 * Inicializa todas las animaciones GSAP
 */
export function initGSAP(deviceData) {
  // initHeroAnimation();
  initParallax();
  initAnimations();
  revealTxt();
  clipBox();
  clipLine();
  initStrokeSvgAnim();

}
